import { Metadata } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ArrowLeftLine,
  EditLine,
  LockLine,
  TeamLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { getBackgroundForTemplate } from "@mirage/mosaics/ComposeAssistant/components/settings/TemplateSettingsModal";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { tagged } from "@mirage/service-logging";
import {
  isPreConfiguredTemplateID,
  type AssistCustomTemplate,
  type AssistTemplate,
} from "@mirage/shared/compose/assist-template";
import { LexicalContentEditable } from "@mirage/shared/compose/editor/LexicalContentEditable";
import { LexicalEditor } from "@mirage/shared/compose/editor/LexicalEditor";
import i18n from "@mirage/translations";
import { memo } from "react";
import { getItemDetails } from "../utils/getItemDetails";
import styles from "./TemplateDetailView.module.css";

const logger = tagged("TemplateDetailView");

interface TemplateDetailViewProps {
  template: AssistTemplate;
  onBack: () => void;
  onUseTemplate: (templateId: string, templateName: string) => void;
  onEditTemplate?: () => void;
}

export const TemplateDetailView = memo(
  ({
    template,
    onBack,
    onUseTemplate,
    onEditTemplate,
  }: TemplateDetailViewProps) => {
    const account = useDropboxAccount();
    const { creatorI18nKey, getAvatarAlt, getProfilePhotoUrl } =
      getItemDetails(template);
    const isPreConfiguredTemplate = isPreConfiguredTemplateID(template.id);
    const avatarAlt = getAvatarAlt(account?.name?.display_name);
    const profilePhotoUrl = getProfilePhotoUrl(account?.profile_photo_url);
    const isOwnedTemplate =
      "accessType" in template && (template as AssistCustomTemplate).isOwned;
    const canEdit = isOwnedTemplate && onEditTemplate;

    return (
      <Box className={styles.TemplateDetailView}>
        <Box className={styles.TemplateDetailViewHeader}>
          <Button
            variant="filled"
            onClick={onBack}
            className={styles.TemplateDetailViewBackButton}
            withIconStart={<UIIcon src={ArrowLeftLine} size="small" />}
          >
            {i18n.t("back")}
          </Button>
        </Box>
        <Box className={styles.TemplateDetailViewBody}>
          <Box className={styles.TemplateDetailViewInfo}>
            <Box className={styles.TemplateDetailViewTitleRow}>
              <Text tagName="h2" className={styles.TemplateDetailViewTitle}>
                <Truncate lines={2}>{template.name}</Truncate>
              </Text>
            </Box>
            {template.description && (
              <Text className={styles.TemplateDetailViewDescription}>
                {template.description}
              </Text>
            )}
            <Metadata size="medium" withDividers="bullet">
              <Metadata.Item>
                <Metadata.Avatar src={profilePhotoUrl} alt={avatarAlt}>
                  {avatarAlt}
                </Metadata.Avatar>
                <Box paddingLeft="Micro XSmall">{i18n.t(creatorI18nKey)}</Box>
              </Metadata.Item>
              <Metadata.Item>
                {i18n.t("ai_tool_library_created_at", {
                  date: new Date().toLocaleDateString(),
                })}
              </Metadata.Item>
              {!isPreConfiguredTemplate && (
                <Metadata.Item>
                  <Metadata.UIIcon
                    src={isOwnedTemplate ? LockLine : TeamLine}
                  />
                  {i18n.t(
                    isOwnedTemplate
                      ? "ai_tool_library_your_templates"
                      : "ai_tool_library_company_templates",
                  )}
                </Metadata.Item>
              )}
            </Metadata>
          </Box>
          <Box className={styles.TemplateDetailViewPreviewWrapper}>
            <Box
              className={styles.TemplateDetailViewPreviewContainer}
              style={{
                background: getBackgroundForTemplate(template),
              }}
            >
              <Box className={styles.TemplateDetailViewPreviewContent}>
                <LexicalEditor
                  namespace="TemplatePreview"
                  editable={false}
                  onError={logger.error}
                  contentEditable={<LexicalContentEditable />}
                  placeholder={null}
                  markdownContent={template.templateContent || ""}
                  onChangeContent={() => {}}
                  isClickableLinks={false}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={styles.TemplateDetailViewFooter}>
          <Box className={styles.TemplateDetailViewActions}>
            {canEdit && (
              <Button
                size="medium"
                onClick={onEditTemplate}
                variant="filled"
                withIconStart={<UIIcon src={EditLine} size="small" />}
              >
                {i18n.t("ai_tool_library_edit_template")}
              </Button>
            )}
            <Button
              onClick={() => onUseTemplate(template.id, template.name || "")}
              variant="primary"
            >
              {i18n.t("ai_tool_library_use_template")}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
);

TemplateDetailView.displayName = "TemplateDetailView";
